import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useFrontendAuth } from '../hooks/useAuth';
import { useDebounce } from '../hooks/useDebounce';
import { searchProducts } from '../services/api.shop';
import SearchSuggestion from './SearchSuggestion';
import { Product, PaginatedResponse } from '../types';
import { Search, Heart, User } from "lucide-react";
import AnimatedPetitoLogo from "./AnimatedPetitoLogo";
import LanguageSwitcher from '../components/LanguageSwitcher';
import NotificationBell from '../components/NotificationBell';
import UserProfileDropdown from '../components/UserProfileDropdown';
import TourHelpButton from './tour/TourHelpButton';
import { useTranslation } from '../context/TranslationContext';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { getFavourites } from '../services/api.shop';

const Header = () => {
    const { t } = useTranslation();
    const { user, loading } = useFrontendAuth();
    const location = useLocation();
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState<Product[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchAttempted, setSearchAttempted] = useState(false);
    const headerRef = useRef<HTMLElement>(null);

    // Avoid double motion: keep header mark still when a larger logo is already animating
    // (login form, guest welcome hero). Animate again on other pages / when logged-in home.
    const path = (location.pathname || '/').toLowerCase();
    const onLogin = path === '/login' || path.startsWith('/login/');
    const onHome = path === '/home' || path === '/';
    const onGuestWelcome = onHome && !user; // includes auth-loading (banner may appear)
    const headerLogoMode: 'static' | 'css' = onLogin || onGuestWelcome ? 'static' : 'css';

    // Same key as ProductCard / Wishlist / Home so badge updates on optimistic toggle
    const { data: favourites = [] } = useProtectedQuery({
        queryKey: ['favourites'],
        queryFn: getFavourites,
        enabled: !!user,
        staleTime: 10 * 60 * 1000,
    });
    const debouncedQuery = useDebounce(searchQuery, 300);
    const navigate = useNavigate();

    // Publish real header height for sticky sub-bars (category filter, etc.)
    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;

        const setHeight = () => {
            document.documentElement.style.setProperty(
                '--shop-header-height',
                `${el.offsetHeight}px`
            );
        };

        setHeight();
        const ro = new ResizeObserver(setHeight);
        ro.observe(el);
        window.addEventListener('resize', setHeight);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', setHeight);
        };
    }, []);

    useEffect(() => {
        const q = debouncedQuery.trim();
        if (q.length < 2) {
            setSuggestions([]);
            setSearchLoading(false);
            setSearchAttempted(false);
            if (q.length === 0) setShowDropdown(false);
            return;
        }

        let cancelled = false;
        setSearchLoading(true);
        setSearchAttempted(false);
        setShowDropdown(true);

        searchProducts({ q, limit: 5 })
            .then((data: PaginatedResponse<Product>) => {
                if (cancelled) return;
                setSuggestions(data.data || []);
            })
            .catch(() => {
                if (cancelled) return;
                setSuggestions([]);
            })
            .finally(() => {
                if (cancelled) return;
                setSearchLoading(false);
                setSearchAttempted(true);
                setShowDropdown(true);
            });

        return () => {
            cancelled = true;
        };
    }, [debouncedQuery]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const q = searchQuery.trim();
        if (q) {
            navigate(`/search?q=${encodeURIComponent(q)}`);
            setShowDropdown(false);
            // Keep typed query for context on results (user can clear themselves)
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        if (e.target.value.trim().length >= 2) setShowDropdown(true);
    };

    const clearQuery = () => {
        setSearchQuery('');
        setShowDropdown(false);
        setSuggestions([]);
        setSearchAttempted(false);
        setSearchLoading(false);
    };

    const showSuggestionPanel =
        showDropdown && searchQuery.trim().length >= 2 && (searchLoading || searchAttempted);

    const renderSuggestionPanel = () => {
        if (!showSuggestionPanel) return null;
        return (
            <div
                className="absolute left-0 right-0 z-[60] mt-1 max-h-[min(50dvh,18rem)] w-full overflow-y-auto overscroll-contain rounded-xl border border-[var(--accent-color)]/50 bg-[var(--bg-color)] shadow-xl sm:max-h-72"
                role="listbox"
                aria-label={t('header.search.placeholder') || 'Search suggestions'}
            >
                {searchLoading && (
                    <div className="flex min-h-11 items-center justify-center gap-2 px-4 py-4 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                        <span
                            className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--accent-color)] border-t-transparent"
                            aria-hidden
                        />
                        {t('header.search.loading') === 'header.search.loading'
                            ? 'Searching\u2026'
                            : t('header.search.loading')}
                    </div>
                )}
                {!searchLoading && searchAttempted && suggestions.length === 0 && (
                    <div className="px-4 py-4 text-center text-sm text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                        {(t('header.search.noresults') === 'header.search.noresults'
                            ? 'No results for \u201C{query}\u201D'
                            : t('header.search.noresults')
                        ).replace('{query}', searchQuery.trim())}
                        <div className="mt-2 text-xs opacity-80">
                            {t('header.search.pressenter', { query: searchQuery })}
                        </div>
                    </div>
                )}
                {!searchLoading &&
                    suggestions.map((product) => (
                        <SearchSuggestion key={product.id} product={product} onClick={clearQuery} />
                    ))}
                {!searchLoading && suggestions.length > 0 && (
                    <button
                        type="submit"
                        className="min-h-11 w-full border-t border-[var(--accent-color)]/40 p-2.5 text-center text-xs font-medium text-[var(--text-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)]"
                    >
                        {t('header.search.pressenter', { query: searchQuery })}
                    </button>
                )}
            </div>
        );
    };

    return (
        <header
            ref={headerRef}
            className="sticky top-0 z-50 bg-[var(--bg-color)] border-b border-[var(--accent-color)] shadow-lg safe-top"
        >
            {/* Figma-style top slogan bar — language pill must fit without clipping (mobile + desktop). */}
            <div className="border-b border-[var(--accent-color)]/40 bg-[var(--bg-color)]/80">
                <div className="mx-auto flex min-h-11 items-center px-4 py-1.5 sm:px-6 lg:px-8">
                    <div className="flex-1" />
                    <span className="hidden text-center text-xs text-[var(--text-color)]/60 sm:block">
                        {"\uD83C\uDF31"} Sustainable parenting made easy
                    </span>
                    <div className="flex flex-1 justify-end">
                        <LanguageSwitcher />
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-14 gap-2 sm:gap-4">
                    <Link to="/home" className="flex items-center gap-2 sm:gap-2.5 flex-shrink-0 min-w-0">
                        {/* UI mark only (not favicons). Static on login + guest welcome to avoid double motion. */}
                        <AnimatedPetitoLogo
                            size="sm"
                            mode={headerLogoMode}
                            playful={headerLogoMode === 'css'}
                            decorative
                        />
                        <h1 className="text-xl font-semibold tracking-tight text-[var(--text-color)] truncate sm:text-2xl">
                            Petito
                        </h1>
                    </Link>

                    {/* Search Bar (Figma compact style) */}
                    <div className="hidden md:flex flex-1 max-w-xl">
                        <form onSubmit={handleSearch} className="relative w-full">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-color)]/50 w-4 h-4" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={handleInputChange}
                                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                placeholder="Search for baby items..."
                                className="w-full rounded-full border border-[var(--accent-color)]/40 bg-[var(--bg-color)] py-2 pl-10 pr-4 text-base text-[var(--text-color)] transition-all focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]/30 sm:text-sm"
                            />
                            {renderSuggestionPanel()}
                        </form>
                    </div>

                    {/* Actions (Figma-style) \u2014 favourites hidden on small screens (bottom nav covers it) */}
                    <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                        <Link
                            to="/favourites"
                            className="relative hidden sm:inline-flex min-h-11 min-w-11 items-center justify-center p-2 hover:bg-[var(--accent-color)]/20 rounded-full transition-colors"
                            title="Favourites"
                        >
                            <Heart className="w-5 h-5 text-[var(--text-color)]" />
                            {user && favourites.length > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                                    {favourites.length > 9 ? '9+' : favourites.length}
                                </span>
                            )}
                        </Link>

                        {user && <NotificationBell />}

                        {user ? (
                            <UserProfileDropdown />
                        ) : loading ? (
                            <button disabled className="min-h-11 min-w-11 p-2 bg-[var(--accent-color)]/20 rounded-full opacity-50 cursor-not-allowed flex items-center justify-center">
                                <User className="w-5 h-5 text-[var(--text-color)]" />
                                <div className="h-5 w-5 animate-spin rounded-full border-t-2 border-b-2 border-[var(--text-color)]"></div>
                            </button>
                        ) : (
                            <>
                                <Link to="/login" className="flex items-center gap-1.5 min-h-11 px-2.5 py-1.5 rounded-full transition-all hover:bg-[var(--accent-color)]/20 text-sm text-[var(--text-color)] font-medium">
                                    Account
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-[var(--text-color)]/50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m6 9 6 6 6-6" /></svg>
                                </Link>
                                <Link to="/login" className="ml-1 px-3 py-1.5 text-xs bg-[var(--accent-color)] text-white rounded-full hover:brightness-95 transition-colors hidden sm:flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" x2="3" y1="12" y2="12"/></svg>
                                    Log in
                                </Link>
                            </>
                        )}

                        {/* Product tour help — between account and Sell; home + my-petito only */}
                        {user && (onHome || path.startsWith('/my-petito') || path.startsWith('/my-rebaby')) && (
                            <TourHelpButton
                                tourId={path.startsWith('/my-petito') || path.startsWith('/my-rebaby') ? 'mypetito' : 'home'}
                            />
                        )}

                        <Link
                            to="/sell"
                            data-tour-id="home.sell"
                            className="hidden sm:inline-flex px-5 py-2 bg-[var(--accent-color)] text-white rounded-full font-medium transition-colors hover:opacity-90 text-sm"
                        >
                            {t('header.sell')}
                        </Link>
                    </div>
                </div>

                {/* Mobile Search */}
                <div className="md:hidden pb-3 pt-1">
                    <form onSubmit={handleSearch} className="relative w-full">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[var(--accent-color)] w-5 h-5" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleInputChange}
                            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                            placeholder={t('header.search.placeholder')}
                            className="w-full rounded-full border border-[var(--accent-color)] bg-[var(--bg-color)] py-2.5 pl-12 pr-4 text-base text-[var(--text-color)] transition-all focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]/30 placeholder:text-[color-mix(in_srgb,var(--text-color),transparent_50%)]"
                            enterKeyHint="search"
                            autoComplete="off"
                        />
                        {renderSuggestionPanel()}
                    </form>
                </div>
            </div>
        </header>
    );
};

export default Header;
